import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import {
  Box,
  Card,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  SelectChangeEvent,
  Toolbar,
  useTheme
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  CollectionVisibility,
  ICollection,
  useAddCollection,
  useModifyCollection
} from "../../../hooks/collections";
import { strings } from "../../../localization/LocalizedStrings";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { TextField } from "../../TextField";
import { AlertDialog } from "../../AlertDialog";
import { SwitchListItem } from "../../SwitchListItem";
import { SelectChipsListItem } from "../../SelectChipsListItem";
import { CollectionBannedFieldsDialog } from "../CollectionBannedFieldsDialog";
import { CollectionBannedFieldsManager } from "../../../models/CollectionBannedFieldsManager";
import { toast } from "react-toastify";
import { FieldTitle } from "../../Text/FieldTitle";
import { ContainedButton } from "../../ContainedButton";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../hooks/isMobile";
import { FreeTextListItem } from "../../FreeTextListItem";
import { ModifyFreeTextAttributeDialog } from "../../attributes/ModifyFreeTextAttributeDialog";
import { ImageDropzone } from "../../ImageDropzone";
import { ImageCache } from "../../../models/ImageCache";
import { useGetImage, useUploadImage } from "../../../hooks/images";
import { Firebase } from "../../../services/Firebase";
import { ProductPurchasesDialog } from "../../products/ProductPurchasesDialog";
import { ProductType } from "../../../hooks/products/interfaces";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons";

export const ModifyCollectionDialog: FunctionComponent<IModifyCollectionDialogProps> = ({
  open,
  onClose,
  collection,
  callback,
  onDelete
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const dialogTitle = collection ? strings.collection_title : strings.collection_new_title;
  const { addCollection } = useAddCollection();
  const { modifyCollection } = useModifyCollection();
  const { uploadImage } = useUploadImage();
  const [isModifyDescriptionDialogOpen, setIsModifyDescriptionDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bannedFieldsDialogOpen, setBannedFieldsDialogOpen] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [purchaseAlertIsOpen, setPurchaseAlertIsOpen] = useState(false);
  const [purchaseDialogIsOpen, setPurchaseDialogIsOpen] = useState(false);
  const [imageFile, setImageFile] = useState<File>();
  const [didChangeImage, setDidChangeImage] = useState(false);
  const { getImage } = useGetImage();
  const [image, setImage] = useState();
  const genericImage = new URL("../../../images/collection.jpg", import.meta.url).href;
  const possibleDiscounts = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
  const fetchImage = async () => {
    if (!collection?.hasImage) return;
    const response = await getImage({
      name: collection.uuid,
      width: ImageCache.bigCollectionImageWidth,
      height: ImageCache.bigCollectionImageHeight
    });
    if (StatusCodesHelper.isSuccessful(response.status)) {
      setImage(response.body);
    }
  };
  useEffect(() => {
    if (!open) return;
    setImage(undefined);
    setDidChangeImage(false);
    fetchImage().then();
  }, [open]);
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onClose();
    }
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.field_missing)
  });
  const formik = useFormik({
    initialValues: {
      name: collection?.name ?? "",
      description: collection?.description ?? "",
      visibility: collection?.visibility ?? "private",
      bannedFields: collection?.bannedFields ?? [],
      discount: collection?.discount ?? 0
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ name, description, visibility, bannedFields, discount }) => {
      setIsLoading(true);
      let statusCode, responseBody;
      if (collection) {
        const { status, body } = await modifyCollection({
          uuid: collection.uuid,
          name: name,
          description: description,
          visibility: visibility,
          bannedFields: bannedFields,
          discount: discount
        });
        statusCode = status;
        responseBody = body;
      } else {
        const { status, body } = await addCollection({
          name: name,
          description: description,
          visibility: visibility,
          bannedFields: bannedFields,
          discount: discount
        });
        statusCode = status;
        responseBody = body;
      }
      if (StatusCodesHelper.isSuccessful(statusCode)) {
        Firebase.shared.logFirebaseEvent("collection_action");
        if (didChangeImage && imageFile && responseBody.uuid) {
          await uploadImage(
            { name: responseBody.uuid, file: imageFile },
            ImageCache.bigCollectionImageWidth,
            ImageCache.bigCollectionImageHeight,
            ImageCache.smallCollectionImageWidth,
            ImageCache.smallCollectionImageHeight
          );
        }
        callback?.();
        onClose();
        toast.success(strings.generic_update_success);
      } else if (StatusCodesHelper.isLimitReached(statusCode)) {
        setAlertMessage(responseBody.message);
        setPurchaseAlertIsOpen(true);
      } else {
        setAlertMessage(responseBody.message);
        setAlertIsOpen(true);
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  const handleVisibilityChange = (
    _event: React.MouseEvent<HTMLElement>,
    visibility: CollectionVisibility
  ) => {
    if (visibility !== null) {
      formik.setFieldValue("visibility", visibility).then();
    }
  };
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"xs"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {collection && (
            <ToolbarButton
              disabled={isLoading}
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <ImageDropzone
            className={styles.dropzone}
            onImageChanged={file => {
              setImageFile(file);
              setDidChangeImage(true);
            }}
            placeholderImage={genericImage}
            initialImage={
              image
                ? `data:image/png;base64,${image}`
                : collection?.thumbnail
                  ? `data:image/png;base64,${collection.thumbnail}`
                  : undefined
            }
            isLoading={false}
          />
          <p className={styles.subtitle} style={{ color: theme.palette.primary.subtitle }}>
            {strings.collection_image_info}
          </p>
          <FieldTitle>{strings.name_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="name"
              name="name"
              size="small"
              autoFocus={!isMobile}
              placeholder={strings.collection_name_placeholder}
              value={formik.values.name}
              onChange={formik.handleChange}
            />
          </div>
          <FieldTitle>{strings.collection_visibility}</FieldTitle>
          {!collection && (
            <p className={styles.subtitle} style={{ color: theme.palette.primary.subtitle }}>
              {strings.collection_create_visibility}
            </p>
          )}
          <SwitchListItem
            className={styles.visibility}
            labels={[strings.private_collection, strings.public_collection]}
            values={["private", "public"]}
            value={formik.values.visibility}
            fullWidth={true}
            disableGutters
            disabled={collection === undefined}
            handleChange={handleVisibilityChange}
          />
          {collection && (
            <FieldArray
              name={"bannedFields"}
              render={arrayHelpers => (
                <div>
                  <FieldTitle>{strings.collection_banned_fields}</FieldTitle>
                  <SelectChipsListItem
                    className={styles.bannedFields}
                    disableGutters
                    title={""}
                    chips={CollectionBannedFieldsManager.shared
                      .getNames(formik.values.bannedFields)
                      .map(name => ({
                        id: name,
                        name: name
                      }))}
                    onAdd={() => setBannedFieldsDialogOpen(true)}
                    onDelete={arrayHelpers.remove}
                  />
                  <CollectionBannedFieldsDialog
                    open={bannedFieldsDialogOpen}
                    setOpen={setBannedFieldsDialogOpen}
                    onSelect={value => {
                      arrayHelpers.push(value);
                      setBannedFieldsDialogOpen(false);
                    }}
                    alreadySelected={formik.values.bannedFields}
                  />
                </div>
              )}
            />
          )}
          <FieldTitle>{strings.collection_discount}</FieldTitle>
          <Select
            value={String(formik.values.discount)}
            renderValue={() => <p>{`${String(formik.values.discount * 100)} %`}</p>}
            className={styles.selectDiscount}
            onChange={(event: SelectChangeEvent) =>
              formik.setFieldValue("discount", +event.target.value)
            }
            sx={{ height: "36px", borderRadius: "12px" }}
          >
            {possibleDiscounts.map((discount, index) => (
              <MenuItem key={`item_${index}`} value={discount}>
                {`${String(discount * 100)} %`}
              </MenuItem>
            ))}
          </Select>
          <FieldTitle>{strings.collection_description}</FieldTitle>
          <Card className={styles.descriptionCard}>
            <FreeTextListItem
              hideTitle
              detail={formik.values.description ?? "--"}
              maxLines={4}
              onClick={() => setIsModifyDescriptionDialogOpen(true)}
            />
          </Card>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <ModifyFreeTextAttributeDialog
        title={strings.collection_description}
        description={strings.edit_description_description}
        placeholder={strings.collection_description}
        value={formik.values.description}
        open={isModifyDescriptionDialogOpen}
        onClose={() => setIsModifyDescriptionDialogOpen(false)}
        onChange={newValue => formik.setFieldValue("description", newValue)}
        onClearValue={() => formik.setFieldValue("description", null)}
      />
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={onClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
      <AlertDialog
        message={alertMessage}
        open={purchaseAlertIsOpen}
        setOpen={setPurchaseAlertIsOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setPurchaseDialogIsOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.COLLECTIONS}
        open={purchaseDialogIsOpen}
        setOpen={setPurchaseDialogIsOpen}
      />
    </FormikProvider>
  );
};

export interface IModifyCollectionDialogProps {
  open: boolean;
  onClose: () => void;
  collection?: ICollection;
  callback?: () => void;
  onDelete?: () => void;
}
